import { BoostPostFormValues } from '@frontend/applications/Social/components/content-preview/types/boost-post-schema';
import {
  BoostPostService,
  BoostPostDependencies,
  BoostPostContext,
  BoostPostResult,
  OptimisticStateContext,
  CreatedResources,
} from '../types/services';
import {
  CampaignCreationResult,
  AdSetCreationResult,
  AdCreationResult,
  EligibilityResult,
  FormValueSetter,
  CreatedResourcesTracker,
} from '../types/boostPost';
import { createErrorHandler } from '../utils/errorHandling';
import { getAdCreationSuccessMessage, getAdStatusCategory } from '../utils/adStatusMessages';

/**
 * Creates a boost post service that handles the complete Meta ad creation workflow
 */
export const createBoostPostService = (deps: BoostPostDependencies): BoostPostService => {
  const errorHandler = createErrorHandler(deps.showErrorMessage, deps.logger);

  /**
   * Creates a campaign for boost post
   */
  const createCampaign = async (
    formValues: BoostPostFormValues,
    createdResources: CreatedResourcesTracker,
    setFormValue?: FormValueSetter,
  ): Promise<CampaignCreationResult> => {
    if (formValues.campaign.type !== 'new') {
      return { success: true, campaignId: formValues.campaign.campaignId };
    }

    try {
      deps.logger.info('Creating new campaign:', {
        name: formValues.campaign.name,
        objective: formValues.campaign.objective,
        adAccountId: formValues.adAccount.id,
      });

      const result = await deps.createCampaignMutation({
        input: {
          name: formValues.campaign.name,
          objective: formValues.campaign.objective,
          adAccountId: formValues.adAccount.id,
        },
      });

      if (result.error) {
        const errorMessage = errorHandler.handleCreationError('campaign', result.error, 'Error creating campaign');
        return { success: false, error: errorMessage };
      }

      const campaignId = result.data?.createMetaAdCampaign?.id;
      if (!campaignId) {
        const errorMessage = 'No campaign ID returned from server';
        errorHandler.handleCreationError('campaign', errorMessage, errorMessage);
        return { success: false, error: errorMessage };
      }

      // Track created resource
      createdResources.campaignId = campaignId;

      // Update optimistic state with raw GraphQL response data
      const optimisticContext: OptimisticStateContext = {
        adAccountId: formValues.adAccount.id,
        campaignType: formValues.campaign.type,
        adSetType: formValues.adSet.type,
        formValues,
      };

      deps.updateOptimisticState(optimisticContext, result.data?.createMetaAdCampaign);

      // Update form state
      if (setFormValue) {
        deps.logger.info('Updating form state after campaign creation:', { campaignId });
        setFormValue('campaign.type', 'existing');
        setFormValue('campaign.campaignId', campaignId);
      }

      errorHandler.logSuccess('campaign', campaignId, { name: formValues.campaign.name });
      deps.showSuccessMessage('Campaign created successfully');
      return { success: true, campaignId };
    } catch (error) {
      const errorMessage = errorHandler.handleCreationError('campaign', error, 'Error creating campaign');
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Creates an ad set for boost post
   */
  const createAdSet = async (
    formValues: BoostPostFormValues,
    campaignId: string,
    createdResources: CreatedResourcesTracker,
    setFormValue?: FormValueSetter,
  ): Promise<AdSetCreationResult> => {
    if (formValues.adSet.type !== 'new') {
      return { success: true, adSetId: formValues.adSet.id };
    }

    try {
      deps.logger.info('Creating new ad set:', {
        name: formValues.adSet.name,
        campaignId,
        adAccountId: formValues.adAccount.id,
      });

      // Convert dollar values to cents for API
      const bidAmountCents =
        formValues.adSet.bidAmount !== undefined ? Math.round(formValues.adSet.bidAmount * 100) : undefined;
      const dailyBudgetCents =
        formValues.adSet.dailyBudget !== undefined ? Math.round(formValues.adSet.dailyBudget * 100) : undefined;
      const lifetimeBudgetCents =
        formValues.adSet.lifetimeBudget !== undefined ? Math.round(formValues.adSet.lifetimeBudget * 100) : undefined;

      const result = await deps.createAdSetMutation({
        input: {
          adAccountId: formValues.adAccount.id,
          adCampaignId: campaignId,
          name: formValues.adSet.name,
          lifetimeBudget: lifetimeBudgetCents,
          dailyBudget: dailyBudgetCents,
          billingEvent: formValues.adSet.billingEvent,
          bidAmount: bidAmountCents,
          startTime: formValues.adSet.startTime?.toISOString(),
          stopTime: formValues.adSet.stopTime?.toISOString(),
        },
      });

      if (result.error) {
        const errorMessage = errorHandler.handleCreationError('adSet', result.error, 'Error creating ad set');
        return { success: false, error: errorMessage };
      }

      const adSetId = result.data?.createMetaAdSet?.id;
      if (!adSetId) {
        const errorMessage = 'No ad set ID returned from server';
        errorHandler.handleCreationError('adSet', errorMessage, errorMessage);
        return { success: false, error: errorMessage };
      }

      // Track created resource
      createdResources.adSetId = adSetId;

      // Update optimistic state with raw GraphQL response data
      const optimisticContext: OptimisticStateContext = {
        adAccountId: formValues.adAccount.id,
        campaignType: formValues.campaign.type,
        adSetType: formValues.adSet.type,
        formValues,
      };

      deps.updateOptimisticState(optimisticContext, undefined, result.data?.createMetaAdSet, campaignId);

      // Update form state
      if (setFormValue) {
        deps.logger.info('Updating form state after ad set creation:', { adSetId });
        setFormValue('adSet.type', 'existing');
        setFormValue('adSet.id', adSetId);
      }

      errorHandler.logSuccess('adSet', adSetId, { name: formValues.adSet.name });
      deps.showSuccessMessage('Ad set created successfully');
      return { success: true, adSetId };
    } catch (error) {
      const errorMessage = errorHandler.handleCreationError('adSet', error, 'Error creating ad set');
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Creates an ad for boost post
   */
  const createAd = async (
    formValues: BoostPostFormValues,
    adSetId: string,
    context: BoostPostContext,
  ): Promise<AdCreationResult> => {
    try {
      // Find Instagram account with pageId and externalId
      const instagramAccount = context.instagramAccounts?.find((account) => account.pageId && account.externalId);

      if (!instagramAccount?.pageId || !instagramAccount?.externalId) {
        const errorMessage = 'No Instagram account found with pageId and externalId';
        errorHandler.handleCreationError('ad', errorMessage, errorMessage);
        return { success: false, error: errorMessage };
      }

      deps.logger.info('Creating new ad:', {
        name: formValues.ad.name,
        adSetId,
        pageId: instagramAccount.pageId,
        externalId: instagramAccount.externalId,
        postId: formValues.post.postExternalId,
      });

      const result = await deps.createAdMutation({
        input: {
          adAccountId: formValues.adAccount.id,
          adSetId,
          name: formValues.ad.name,
          callToActionUrl: formValues.ad.callToActionUrl,
          callToActionType: formValues.ad.callToActionType,
          sourceInstagramMediaId: formValues.post.postExternalId,
          advertiserFacebookPageId: instagramAccount.pageId,
          advertiserInstagramAccountId: instagramAccount.externalId,
        },
      });

      if (result.error) {
        const errorMessage = errorHandler.handleCreationError('ad', result.error, 'Error creating ad');
        return { success: false, error: errorMessage };
      }

      const adId = result.data?.createMetaAdFromExistingPost?.id;
      const adStatus = result.data?.createMetaAdFromExistingPost?.status;

      if (!adId) {
        const errorMessage = 'No ad ID returned from server';
        errorHandler.handleCreationError('ad', errorMessage, errorMessage);
        return { success: false, error: errorMessage };
      }

      errorHandler.logSuccess('ad', adId, { name: formValues.ad.name, status: adStatus });
      return { success: true, adId, adStatus };
    } catch (error) {
      const errorMessage = errorHandler.handleCreationError('ad', error, 'Error creating ad');
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Validates boost post eligibility
   */
  const validateBoostEligibility = (formValues: BoostPostFormValues, context: BoostPostContext): EligibilityResult => {
    // Work directly with InstagramAccount GraphQL type
    const instagramAccount = context.instagramAccounts?.find((account) => account.pageId && account.externalId);

    if (!instagramAccount?.pageId || !instagramAccount?.externalId) {
      return {
        eligible: false,
        error: 'No Instagram account found with a pageId and externalId',
      };
    }

    if (!formValues.post.postExternalId) {
      return {
        eligible: false,
        error: 'Post external ID is required',
      };
    }

    return { eligible: true };
  };

  /**
   * Creates a complete boost from post workflow
   */
  const createBoostFromPost = async (
    formValues: BoostPostFormValues,
    context: BoostPostContext,
    setFormValue?: FormValueSetter,
  ): Promise<BoostPostResult> => {
    const createdResources: CreatedResourcesTracker = {};

    deps.logger.info('Starting boost post creation workflow:', {
      adAccountId: formValues.adAccount.id,
      campaignType: formValues.campaign.type,
      adSetType: formValues.adSet.type,
      postId: formValues.post.postExternalId,
    });

    // Validate eligibility
    const eligibility = validateBoostEligibility(formValues, context);
    if (!eligibility.eligible) {
      deps.logger.error('Boost post eligibility check failed:', eligibility.error);
      return { success: false, error: eligibility.error, createdResources };
    }

    // Step 1: Create campaign
    const campaignResult = await createCampaign(formValues, createdResources, setFormValue);
    if (!campaignResult.success) {
      deps.logger.error('Campaign creation failed, aborting workflow');
      errorHandler.handlePartialFailure(createdResources, setFormValue);
      return { success: false, error: campaignResult.error, createdResources };
    }

    // Step 2: Create ad set
    if (!campaignResult.campaignId) {
      deps.logger.error('Campaign creation succeeded but no campaign ID returned');
      errorHandler.handlePartialFailure(createdResources, setFormValue);
      return { success: false, error: 'No campaign ID returned', createdResources };
    }

    const adSetResult = await createAdSet(formValues, campaignResult.campaignId, createdResources, setFormValue);
    if (!adSetResult.success) {
      deps.logger.error('Ad set creation failed, aborting workflow');
      errorHandler.handlePartialFailure(createdResources, setFormValue);
      return { success: false, error: adSetResult.error, createdResources };
    }

    // Step 3: Create ad
    if (!adSetResult.adSetId) {
      deps.logger.error('Ad set creation succeeded but no ad set ID returned');
      errorHandler.handlePartialFailure(createdResources, setFormValue);
      return { success: false, error: 'No ad set ID returned', createdResources };
    }

    const adResult = await createAd(formValues, adSetResult.adSetId, context);
    if (!adResult.success) {
      deps.logger.error('Ad creation failed, workflow completed with partial success');
      errorHandler.handlePartialFailure(createdResources, setFormValue);
      return { success: false, error: adResult.error, createdResources };
    }

    // Success!
    const finalResources: CreatedResources = {
      campaignId: campaignResult.campaignId,
      adSetId: adSetResult.adSetId,
      adId: adResult.adId,
    };

    // Log with status information
    deps.logger.info('Boost post workflow completed successfully:', {
      ...finalResources,
      adStatus: adResult.adStatus,
      statusCategory: adResult.adStatus ? getAdStatusCategory(adResult.adStatus) : 'unknown',
    });

    // Determine conditional success message
    let successMessage: string;
    if (adResult.adStatus) {
      successMessage = getAdCreationSuccessMessage(adResult.adStatus);
    } else {
      // Fallback to generic message if status is unavailable
      deps.logger.warn('Ad status not available, using generic success message');
      successMessage = 'Ad created successfully';
    }

    return {
      success: true,
      createdResources: finalResources,
      successMessage,
    };
  };

  return {
    createBoostFromPost,
    validateBoostEligibility,
  };
};
